import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search, History, Pencil, Trash2 } from "lucide-react";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import { fetchSupplies, deleteSupply } from "../../apis/api.inventory";
import { queryInventorySuppliesKey } from "../../apis/queryKeys";
import { formatAmount } from "../../utils/inventoryFormat";
import { formatDateOnly, getExpirationStatus, todayISODate } from "../../utils/inventoryExpiration";
import customToast from "../common/Toaster";
import { Input } from "../common/Input";
import Button from "../common/Button";
import Spinner from "../common/Spinner";
import SupplyHistoryDialog from "./SupplyHistoryDialog";

// Listado de insumos con buscador por descripción, historial y edición.
export default function SupplyList() {
  const { t } = useBrandTranslation();
  const { ownerId } = useInventoryOwner();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const [historySupply, setHistorySupply] = useState(null);

  const { decimalSeparator, defaultCurrencySymbol } = useInventorySettings(ownerId);

  const { data: supplies, isLoading } = useQuery({
    queryKey: queryInventorySuppliesKey(ownerId, search),
    queryFn: () => fetchSupplies(ownerId, search),
    enabled: !!ownerId,
  });

  const deleteMutation = useMutation({
    mutationFn: (supplyId) => deleteSupply(supplyId, ownerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      customToast(t("inventory.list.deleted", "Insumo eliminado"), "success");
    },
    onError: (e) => customToast(e?.message || "Error", "danger"),
  });

  const handleDelete = (supply) => {
    if (window.confirm(t("inventory.list.confirmDelete", "¿Estás seguro que deseas eliminar este insumo?"))) {
      deleteMutation.mutate(supply.id);
    }
  };

  const isLow = (s) => Number(s.stock) <= Number(s.reorder_threshold);
  const today = todayISODate();
  const expirationClass = {
    expired: "bg-red-100 text-red-700",
    expiring: "bg-amber-100 text-amber-800",
    ok: "bg-green-100 text-green-700",
  };

  return (
    <div>
      <div className="relative mb-3">
        <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
        <Input
          className="pl-9"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("inventory.list.searchPlaceholder", "Buscar insumo por descripción…")}
        />
      </div>

      {isLoading ? (
        <Spinner className="w-8 h-8 mx-auto my-6" />
      ) : !supplies || supplies.length === 0 ? (
        <p className="text-gray-500 text-sm py-6 text-center">
          {t("inventory.list.empty", "Aún no tienes insumos cargados")}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b">
                <th className="py-2 pr-3">{t("inventory.list.description", "Descripción")}</th>
                <th className="py-2 pr-3 whitespace-nowrap">{t("inventory.list.lastUpdate", "Última actualización")}</th>
                <th className="py-2 pr-3">{t("inventory.list.stock", "Stock")}</th>
                <th className="py-2 pr-3">{t("inventory.list.expiration", "Vence")}</th>
                <th className="py-2 pr-3 text-right">{t("inventory.list.actions", "Acciones")}</th>
              </tr>
            </thead>
            <tbody>
              {supplies.map((s) => (
                <tr key={s.id} className="border-b last:border-0">
                  <td className="py-2 pr-3 font-medium text-gray-800">{s.description}</td>
                  <td className="py-2 pr-3 whitespace-nowrap text-gray-500">
                    {s.updated_at ? new Date(s.updated_at).toLocaleDateString() : "—"}
                  </td>
                  <td className="py-2 pr-3">
                    <span
                      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                        isLow(s) ? "bg-red-100 text-red-700" : "bg-green-100 text-green-700"
                      }`}
                    >
                      {formatAmount(s.stock, decimalSeparator)}
                    </span>
                  </td>
                  <td className="py-2 pr-3 whitespace-nowrap">
                    {s.expiration_date ? (
                      <span
                        className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                          expirationClass[getExpirationStatus(s, today)]
                        }`}
                      >
                        {formatDateOnly(s.expiration_date)}
                      </span>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center justify-end gap-1">
                      <button
                        type="button"
                        title={t("inventory.list.history", "Historial")}
                        onClick={() => setHistorySupply(s)}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-600"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title={t("inventory.list.edit", "Editar")}
                        onClick={() => navigate(`/inventory/supplies/${s.id}/edit`)}
                        className="p-1.5 rounded hover:bg-gray-100 text-blue-600"
                      >
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button
                        type="button"
                        title={t("inventory.list.delete", "Eliminar")}
                        onClick={() => handleDelete(s)}
                        className="p-1.5 rounded hover:bg-gray-100 text-red-600"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SupplyHistoryDialog
        supply={historySupply}
        ownerId={ownerId}
        decimalSeparator={decimalSeparator}
        currencySymbol={defaultCurrencySymbol}
        onClose={() => setHistorySupply(null)}
      />

      <div className="mt-4 flex justify-end">
        <Button variant="outline" onClick={() => navigate("/inventory/supplies/new")}>
          {t("inventory.nav.newSupply", "+ Insumo")}
        </Button>
      </div>
    </div>
  );
}
